import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import {
  fetchVehicleReservations,
  postReservationsByState,
  putReservationState,
  updateVehicleReservationStatus,
} from "@/apis/reservation";
import { postUsersByState, putUserState } from "@/apis/user";
import Layout from "@/components/Layout";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import { ADMIN_MAIN_URL, HOME_MAIN_URL } from "@/constants/routes";
import useModal from "@/hooks/useModal";
import {
  AdminPendingReservations,
  AdminPendingUsers,
  AdminPendingVehicles,
  AdminTodayReservations,
} from "@/pages/Dashboard/components/AdminSections";
import DashboardCard from "@/pages/Dashboard/components/DashboardCard";
import useUserStore from "@/store/userStore";
import type { ReservationByState } from "@/types/reservation";
import type { UserByState } from "@/types/user";
import type { VehicleReservation } from "@/types/vehicleReservation";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const user = useUserStore();

  const [pendingUsers, setPendingUsers] = useState<UserByState[]>([]);
  const [pendingReservations, setPendingReservations] = useState<
    ReservationByState[]
  >([]);
  const [pendingVehicles, setPendingVehicles] = useState<VehicleReservation[]>(
    [],
  );
  const [todayReservations, setTodayReservations] = useState<
    ReservationByState[]
  >([]);
  const [loading, setLoading] = useState(true);

  const confirmModal = useModal();
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, reservationsRes, vehiclesRes, todayRes] =
        await Promise.all([
          postUsersByState(0, 100, "00"),
          postReservationsByState(0, 100, "request"),
          fetchVehicleReservations(dayjs().format("YYYYMM"), "pending"),
          postReservationsByState(
            0,
            10,
            "approve",
            "all",
            dayjs().format("YYYYMMDD"),
            dayjs().format("YYYYMMDD"),
          ),
        ]);

      setPendingUsers(usersRes?.content || []);
      setPendingReservations(reservationsRes?.content || []);
      setPendingVehicles(vehiclesRes || []);
      setTodayReservations(todayRes?.content || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
  ) => {
    setConfirmAction({ title, message, onConfirm });
    confirmModal.onOpen();
  };

  const handleConfirm = async () => {
    if (confirmAction) {
      await confirmAction.onConfirm();
      confirmModal.onClose();
      setConfirmAction(null);
    }
  };

  const approveUser = (userId: string, userName: string) => {
    showConfirm("회원 승인", `${userName}님을 승인하시겠습니까?`, async () => {
      await putUserState({ userId, userStateCode: "01" });
      await loadData();
    });
  };

  const rejectUser = (userId: string, userName: string) => {
    showConfirm("회원 반려", `${userName}님을 반려하시겠습니까?`, async () => {
      await putUserState({ userId, userStateCode: "09" });
      await loadData();
    });
  };

  const approveReservation = (rsvtId: number, plcNm: string) => {
    showConfirm("예약 승인", `${plcNm} 예약을 승인하시겠습니까?`, async () => {
      await putReservationState({ rsvtId, sttCd: "approve" });
      await loadData();
    });
  };

  const rejectReservation = (rsvtId: number, plcNm: string) => {
    showConfirm("예약 반려", `${plcNm} 예약을 반려하시겠습니까?`, async () => {
      await putReservationState({ rsvtId, sttCd: "cancel" });
      await loadData();
    });
  };

  const approveVehicle = (id: number, destination: string) => {
    showConfirm(
      "차량 예약 승인",
      `${destination} 차량 예약을 승인하시겠습니까?`,
      async () => {
        await updateVehicleReservationStatus(id, "approved");
        await loadData();
      },
    );
  };

  const rejectVehicle = (id: number, destination: string) => {
    showConfirm(
      "차량 예약 반려",
      `${destination} 차량 예약을 반려하시겠습니까?`,
      async () => {
        await updateVehicleReservationStatus(id, "rejected");
        await loadData();
      },
    );
  };

  const goToAdminTab = (tab: number) => {
    navigate(`${ADMIN_MAIN_URL}?tab=${tab}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center text-gray-400">
          로딩 중...
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            {user.userName}님, 안녕하세요
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {dayjs().format("YYYY년 M월 D일 (dd)")}
          </p>
        </div>

        {/* Big Cards */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <DashboardCard
            title="장소 예약 승인 대기"
            count={pendingReservations.length}
            label="건의 장소 예약 요청"
            color="blue"
            onClick={() => goToAdminTab(1)}
          />
          <DashboardCard
            title="차량 예약 승인 대기"
            count={pendingVehicles.length}
            label="건의 차량 예약 요청"
            color="green"
            onClick={() => goToAdminTab(2)}
          />
        </div>

        {/* Small Cards */}
        <div className="grid grid-cols-2 gap-3">
          <DashboardCard
            title="회원가입 승인 대기"
            count={pendingUsers.length}
            label="명이 승인을 기다리고 있습니다"
            color="amber"
            onClick={() => goToAdminTab(0)}
          />
          <DashboardCard
            title="오늘 승인된 예약"
            count={todayReservations.length}
            label="건의 장소 예약이 오늘 진행"
            color="red"
            onClick={() => navigate(HOME_MAIN_URL)}
          />
        </div>

        {/* Detail Sections */}
        <div className="grid gap-4 lg:grid-cols-2">
          <AdminPendingReservations
            reservations={pendingReservations}
            onViewAll={() => goToAdminTab(1)}
            onApprove={approveReservation}
            onReject={rejectReservation}
          />
          <AdminPendingVehicles
            vehicles={pendingVehicles}
            onViewAll={() => goToAdminTab(2)}
            onApprove={approveVehicle}
            onReject={rejectVehicle}
          />
          <AdminPendingUsers
            users={pendingUsers}
            onViewAll={() => goToAdminTab(0)}
            onApprove={approveUser}
            onReject={rejectUser}
          />
          <AdminTodayReservations
            reservations={todayReservations}
            onViewCalendar={() => navigate(HOME_MAIN_URL)}
          />
        </div>
      </div>

      {confirmModal.isOpen && confirmAction && (
        <ConfirmModal
          title={confirmAction.title}
          onConfirm={handleConfirm}
          onClose={() => {
            confirmModal.onClose();
            setConfirmAction(null);
          }}
        >
          {confirmAction.message}
        </ConfirmModal>
      )}
    </Layout>
  );
};

export default AdminDashboard;
